import React, { useEffect, useState } from 'react';
import {
  Container, Paper, Typography, Stack, Button, Alert, CircularProgress, Box, Divider
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pickServiceName } from '../../utils/serviceName';
import { formatMoney } from '../../utils/money';
import { api } from '../../api';
import { TermsAcceptField, TERMS_VERSION } from '../../components/Terms';
import dayjs from 'dayjs';

export default function ConfirmByLink() {
  const { t, i18n } = useTranslation();
  const { hash } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [terms, setTerms] = useState(false);
  const [acceptedTermsVersion, setAcceptedTermsVersion] = useState(TERMS_VERSION);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.getBookingConfirm(hash)
      .then(setData)
      .catch((e) => setError(e.message || t('confirm.linkInvalid')))
      .finally(() => setLoading(false));
  }, [hash]);

  const total = data && Array.isArray(data.services)
    ? data.services.filter(Boolean).reduce((s, x) => s + Number(x.price || 0), 0) : 0;

  const confirm = async () => {
    setError('');
    if (!terms) { setError(t('confirm.mustAcceptTerms')); return; }
    setBusy(true);
    try {
      await api.submitBookingConfirm(hash, { termsAccepted: true, termsVersion: acceptedTermsVersion });
      setDone(true);
    } catch (e) {
      setError(e.message || t('confirm.failed'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Container sx={{ mt: 8, textAlign: 'center' }}><CircularProgress color="primary" /></Container>;
  }

  if (done) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64 }} />
            <Typography variant="h5">{t('confirm.doneTitle')}</Typography>
            <Typography color="text.secondary">{t('confirm.doneSubtitle')}</Typography>
          </Stack>
        </Paper>
      </Container>
    );
  }

  if (error && !data) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}><Alert severity="error">{error}</Alert></Paper>
      </Container>
    );
  }

  const start = dayjs(`${data.date}T${String(data.time).slice(0, 5)}`);

  return (
    <Container maxWidth="sm" sx={{ mt: 6 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>{t('confirm.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('confirm.subtitle')}
        </Typography>

        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography><b>{t('confirm.dateTimeLabel')}</b> {t('confirm.dateAtTime', { date: start.format('DD.MM.YYYY'), time: start.format('HH:mm') })}</Typography>
          <Typography><b>{t('confirm.masterLabel')}</b> {data.masterName || '—'}</Typography>
          <Typography><b>{t('confirm.clientLabel')}</b> {data.clientName || '—'}</Typography>
          <Typography><b>{t('confirm.servicesLabel')}</b> {data.services.filter(Boolean).map((s) => pickServiceName(s, i18n.language)).join(', ')}</Typography>
          <Typography color="text.secondary">{data.durationMinutes} {t('common.min')} · {formatMoney(total)}</Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 1 }}>
          <TermsAcceptField
            checked={terms}
            onChange={setTerms}
            workspaceId={data.workspaceId}
            onTerms={(d) => setAcceptedTermsVersion(String(d.version ?? TERMS_VERSION))}
          />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Button variant="contained" fullWidth onClick={confirm} disabled={busy}>
          {busy ? t('confirm.confirming') : t('confirm.confirmButton')}
        </Button>
      </Paper>
    </Container>
  );
}
